import React, { useState } from 'react';
import { Button, Modal, Form, DatePicker, InputNumber, Select, Space, Typography, message, Row, Col, Alert } from 'antd';
import moment from 'moment';
import { userService } from '../../../services/api';

const { Title } = Typography;

function AddPaymentModal({ invoice, onCreated, onError, open, onOpenChange, hideTrigger = false, title = 'Agregar pago', prefillPayment = null }) {
  const canAdd =
    !!invoice &&
    invoice.status === 'timbrada' &&
    invoice.tipo_comprobante === 'I' &&
    invoice.metodo_pago === 'PPD';

  const isControlled = typeof open === 'boolean';
  const [visibleInternal, setVisibleInternal] = useState(false);
  const visible = isControlled ? open : visibleInternal;
  const setVisible = (next) => {
    if (typeof onOpenChange === 'function') {
      onOpenChange(next);
    }
    if (!isControlled) {
      setVisibleInternal(next);
    }
  };
  const [adding, setAdding] = useState(false);
  const [dateWarning, setDateWarning] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [form] = Form.useForm();

  const [totalPaid, setTotalPaid] = useState(0);
  const invoiceTotal = Number(invoice?.total || 0);
  const [drDisabled, setDrDisabled] = useState(true);
  const [nextParcialidadState, setNextParcialidadState] = useState(1);
  const [previousSaldoInsoluto, setPreviousSaldoInsoluto] = useState(invoiceTotal);

  // Exponer método para abrir el modal desde fuera (ej. para editar)
  React.useEffect(() => {
    if (invoice && invoice.openPaymentModal) {
        invoice.openPaymentModal = (paymentToEdit) => openModal(paymentToEdit);
    }
  }, [invoice]);

  React.useEffect(() => {
    if (visible && invoice) {
      initialiseModalFields(prefillPayment, { createFromSource: !!prefillPayment });
    }
  }, [visible, invoice, prefillPayment]);

  if (!invoice || !canAdd) {
    return null;
  }

  // Función independiente para validar FechaPago según reglas fiscales
  const validatePaymentDate = (paymentDate, invoiceDate) => {
    if (!paymentDate) return Promise.resolve();
    
    const pDate = moment(paymentDate.toDate ? paymentDate.toDate() : paymentDate);
    // const iDate = moment(invoiceDate); // Ya no se valida contra la fecha de emisión
    const now = moment();

    // 1. No permitir FechaPago futura
    if (pDate.isAfter(now, 'day')) {
      return Promise.reject(new Error('La fecha de pago no puede ser futura.'));
    }

    // 2. Validación de fecha anterior a emisión eliminada por solicitud
    /*
    if (pDate.isBefore(iDate, 'day')) {
      return Promise.reject(new Error(`La fecha de pago no puede ser anterior a la fecha de emisión del CFDI PPD (${iDate.format('YYYY-MM-DD')}).`));
    }
    */

    // 3. Advertencia si está fuera del plazo del día 10 natural del mes siguiente
    // Regla fiscal: El REP debe emitirse a más tardar el quinto día natural del mes siguiente (actualizado a día 10 en versiones recientes o por facilidades)
    // Aquí validamos si la fecha actual (emisión del REP) es posterior al día 10 del mes siguiente a la FechaPago.
    const deadline = pDate.clone().add(1, 'month').startOf('month').add(9, 'days').endOf('day'); // Día 10
    
    if (now.isAfter(deadline)) {
      setDateWarning('Aviso: Estás generando el complemento fuera del plazo sugerido (día 10 del mes siguiente al pago). Esto podría tener implicaciones fiscales.');
    } else {
      setDateWarning(null);
    }

    return Promise.resolve();
  };

  const fetchTotalPaidForInvoice = async () => {
    try {
      const resp = await userService.getPayments(200, 0, { invoice_id: invoice?.id });
      const raw = resp?.data?.payments ?? resp?.payments ?? [];
      const list = (Array.isArray(raw) ? raw : []).filter(
        (p) =>
          p?.status === 'timbrada' &&
          Array.isArray(p?.documents) &&
          p.documents.some((d) => d?.invoice?.id === invoice?.id)
      );
      const docs = list.flatMap((p) => (p.documents || []).filter((d) => d?.invoice?.id === invoice?.id));
      const sum = docs.reduce((s, d) => s + Number(d?.importe_pagado || 0), 0);
      const normalized = Number(sum.toFixed(2));
      setTotalPaid(normalized);

      let maxParcialidad = 0;
      let lastSaldoInsoluto = invoiceTotal;
      docs.forEach((d) => {
        const np = Number(d?.num_parcialidad || 0);
        if (np > maxParcialidad) {
          maxParcialidad = np;
          lastSaldoInsoluto = Number(d?.saldo_insoluto ?? lastSaldoInsoluto);
        }
      });
      return { totalPaid: normalized, nextParcialidad: maxParcialidad + 1, previousSaldoInsoluto: Number(lastSaldoInsoluto) };
    } catch (e) {
      return { totalPaid: 0, nextParcialidad: 1, previousSaldoInsoluto: invoiceTotal };
    }
  };

  const initialiseModalFields = async (paymentToEdit = null, options = {}) => {
    if (!invoice) return;
    if (paymentToEdit && paymentToEdit.nativeEvent) {
      paymentToEdit = null;
    }
    const createFromSource = options.createFromSource === true;
    setEditingPayment(createFromSource ? null : paymentToEdit);
    const { totalPaid: paid, nextParcialidad, previousSaldoInsoluto: prevSaldo } = await fetchTotalPaidForInvoice();
    setNextParcialidadState(nextParcialidad || 1);
    setPreviousSaldoInsoluto(prevSaldo || invoiceTotal);
    const remaining = Math.max(prevSaldo || 0, 0);
    const invoiceCurrency = invoice?.moneda || 'MXN';

    if (paymentToEdit) {
      const doc = paymentToEdit.documents ? paymentToEdit.documents[0] : {};
      form.setFieldsValue({
        fecha_pago: moment(paymentToEdit.fecha_pago),
        forma_pago: paymentToEdit.forma_pago,
        moneda: paymentToEdit.moneda,
        tipo_cambio: Number(paymentToEdit.tipo_cambio),
        monto: Number(paymentToEdit.monto),
        moneda_dr: doc.moneda_dr,
        tipo_cambio_dr: Number(doc.tipo_cambio_dr),
        num_parcialidad: doc.num_parcialidad,
        saldo_anterior: Number(doc.saldo_anterior),
        importe_pagado: Number(doc.importe_pagado),
        saldo_insoluto: Number(doc.saldo_insoluto),
      });
    } else {
      form.setFieldsValue({
        fecha_pago: null,
        forma_pago: '03',
        moneda: 'MXN',
        tipo_cambio: 1,
        monto: Number(remaining.toFixed(2)),
        moneda_dr: invoiceCurrency,
        tipo_cambio_dr: 1,
        num_parcialidad: nextParcialidad || 1,
        saldo_anterior: Number(remaining.toFixed(2)),
        importe_pagado: Number(remaining.toFixed(2)),
        saldo_insoluto: 0,
      });
    }
    setDrDisabled(form.getFieldValue('moneda') === form.getFieldValue('moneda_dr'));
    setDateWarning(null);
  };

  const openModal = async (paymentToEdit = null) => {
    await initialiseModalFields(paymentToEdit);
    setVisible(true);
  };


  const handleValuesChange = (changedValues, allValues) => {
    const remainingBefore = Number(allValues.saldo_anterior ?? 0);
    
    let updates = {};

    // Lógica de monedas y tipos de cambio
    if ('moneda' in changedValues || 'tipo_cambio' in changedValues || 'moneda_dr' in changedValues || 'tipo_cambio_dr' in changedValues) {
        const monedaP = changedValues.moneda || allValues.moneda;
        const monedaDR = allValues.moneda_dr;
        const tipoCambioP = changedValues.tipo_cambio || allValues.tipo_cambio;

        // Si moneda pago es MXN, tipo cambio pago es 1
        if (monedaP === 'MXN') {
            updates.tipo_cambio = 1;
        }

        // Si monedas son iguales, tipo cambio DR (Equivalencia) es 1
        if (monedaP === monedaDR) {
            updates.tipo_cambio_dr = 1;
            setDrDisabled(true);
        } 
        // Si Pago es Extranjera y Doc es MXN, Equivalencia suele ser el TipoCambioP
        else if (monedaP !== 'MXN' && monedaDR === 'MXN') {
            updates.tipo_cambio_dr = tipoCambioP;
            setDrDisabled(false);
        } else {
            setDrDisabled(false);
        }
    }
    
    // Si cambió el monto, actualizamos importe_pagado automáticamente
    if ('monto' in changedValues) {
        const newMonto = Number(changedValues.monto || 0);
        // El importe pagado no puede exceder el saldo restante
        const newImportePagado = Math.min(newMonto, remainingBefore);
        const newSaldoIns = Math.max(remainingBefore - newImportePagado, 0);
        
        updates = { ...updates,
            importe_pagado: Number(newImportePagado.toFixed(2)),
            saldo_anterior: Number(remainingBefore.toFixed(2)),
            saldo_insoluto: Number(newSaldoIns.toFixed(2)),
        };
    } else {
        const importe = Number(allValues.importe_pagado || 0);
        const saldoIns = Math.max(remainingBefore - importe, 0);
        updates = { ...updates,
          saldo_anterior: Number(remainingBefore.toFixed(2)),
          saldo_insoluto: Number(saldoIns.toFixed(2)),
        };
    }

    if (Object.keys(updates).length > 0) {
        form.setFieldsValue(updates);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const monedaP = values.moneda;
      const monedaDR = values.moneda_dr;
      const tipoCambioP = Number(values.tipo_cambio);
      const tipoCambioDR = Number(values.tipo_cambio_dr);
      const saldoAnterior = Number(values.saldo_anterior || 0);
      const importePagado = Number(values.importe_pagado || 0);
      const montoPago = Number(values.monto || 0);

      if (monedaP === 'MXN' && tipoCambioP !== 1) {
        form.setFieldsValue({ tipo_cambio: 1 });
      }
      if (monedaP !== 'MXN' && (!tipoCambioP || tipoCambioP <= 0)) {
        message.error('El tipo de cambio debe ser mayor a 0.');
        return;
      }
      if (monedaP === monedaDR) {
        if (tipoCambioDR !== 1) {
          form.setFieldsValue({ tipo_cambio_dr: 1 });
        }
      } else if (!tipoCambioDR || tipoCambioDR <= 0) {
        message.error('El tipo de cambio del documento (DR) debe ser mayor a 0.');
        return;
      }
      if (importePagado > saldoAnterior + 0.00001) {
        message.error(`No puedes pagar más del saldo disponible: ${saldoAnterior.toFixed(2)}`);
        return;
      }
      if (montoPago > saldoAnterior + 0.00001) {
        message.error(`El monto del pago excede el saldo disponible: ${saldoAnterior.toFixed(2)}`);
        return;
      }

      const payload = {
        company_id: invoice.company?.id,
        receiver_id: invoice.receiver?.id,
        fecha_pago: values.fecha_pago?.format?.('YYYY-MM-DD'),
        forma_pago: values.forma_pago,
        moneda: values.moneda,
        tipo_cambio: values.tipo_cambio,
        monto: values.monto,
        doctos_relacionados: [
          {
            invoice_id: invoice.id,
            moneda_dr: values.moneda_dr,
            num_parcialidad: values.num_parcialidad,
            saldo_anterior: values.saldo_anterior,
            importe_pagado: values.importe_pagado,
            saldo_insoluto: values.saldo_insoluto,
            tipo_cambio_dr: values.tipo_cambio_dr,
            metodo_pago_dr: 'PPD',
          },
        ],
      };
      setAdding(true);

      // Crear o Actualizar pago (solo borrador)
      const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
      const requestWithRetry = async (fn, retries = 2, baseDelayMs = 400) => {
        let lastErr;
        for (let attempt = 0; attempt <= retries; attempt++) {
          try {
            return await fn();
          } catch (e) {
            lastErr = e;
            const isNetwork = !e?.response && (e?.message === 'Network Error' || e?.code === 'ERR_NETWORK');
            if (!isNetwork || attempt === retries) break;
            await sleep(baseDelayMs * Math.pow(2, attempt));
          }
        }
        throw lastErr;
      };

      const resp = await requestWithRetry(() => {
        if (editingPayment) return userService.updatePayment(editingPayment.id, payload);
        return userService.createPayment(payload);
      });
      if (resp?.error) {
        throw new Error(resp.error);
      }
      
      const payment = resp?.data || resp;
      if (!payment?.id) {
        throw new Error('Respuesta inválida del servidor al guardar el pago.');
      }
      
      message.success(
        editingPayment
          ? 'Pago actualizado correctamente'
          : prefillPayment
          ? 'Pago sustituto guardado como borrador'
          : 'Pago guardado como borrador'
      );
      
      setVisible(false);
      
      if (typeof onCreated === 'function') {
        onCreated(payment);
      }
      
    } catch (err) {
      console.error('Error al guardar el pago:', err);
      
      const status = err?.response?.status;
      let errMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Error al guardar el pago';
      if (status === 401) {
        errMsg = 'Tu sesión expiró. Vuelve a iniciar sesión.';
      } else if (status === 403) {
        errMsg = 'No tienes permisos para realizar esta acción.';
      } else if (!err?.response && (err?.message === 'Network Error' || err?.code === 'ERR_NETWORK')) {
        errMsg = 'Error de red. Verifica tu conexión e inténtalo de nuevo.';
      }
        
      // Si el mensaje es del tipo "Error HTTP: {...} - {...}", extraemos el JSON y el mensaje interno
      if (typeof errMsg === 'string' && errMsg.startsWith('Error HTTP:')) {
        try {
          const jsonPart = errMsg.substring('Error HTTP:'.length, errMsg.indexOf(' - {')).trim();
          const errorObj = JSON.parse(jsonPart);
          if (errorObj && errorObj.message) {
            errMsg = errorObj.message;
          }
        } catch (e) {
          console.error('Error parsing error message:', e);
        }
      }
      
      message.error(errMsg);
      if (typeof onError === 'function') {
        onError(errMsg, err);
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      {!hideTrigger && <Button onClick={openModal}>Agregar pago</Button>}

      <Modal
        title={title}
        open={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        destroyOnClose
        width={800}
      >
        <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
          {!!prefillPayment && (
            <Alert
              message="Sustitución de pago"
              description={`Se creará un nuevo borrador basado en el pago ${prefillPayment?.serie || '—'}-${prefillPayment?.folio || '—'}${prefillPayment?.uuid ? ` (${prefillPayment.uuid})` : ''}. Timbra el nuevo pago y después cancela el incorrecto con motivo 01.`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          {dateWarning && (
            <Alert
              message={dateWarning}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Fecha de pago"
                name="fecha_pago"
                rules={[
                  { required: true, message: 'Selecciona la fecha de pago' },
                  {
                    validator: (_, value) => validatePaymentDate(value, invoice?.created_at)
                  }
                ]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Forma de pago"
                name="forma_pago"
                rules={[{ required: true, message: 'Selecciona la forma de pago' }]}
              >
                <Select
                  options={[
                    { value: '01', label: '01 - Efectivo' },
                    { value: '02', label: '02 - Cheque nominativo' },
                    { value: '03', label: '03 - Transferencia electrónica' },
                    { value: '04', label: '04 - Tarjeta de crédito' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Moneda"
                name="moneda"
                rules={[{ required: true, message: 'Selecciona la moneda' }]}
              >
                <Select
                  options={[
                    { value: 'MXN', label: 'MXN - Peso mexicano' },
                    { value: 'USD', label: 'USD - Dólar estadounidense' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Tipo de cambio"
                name="tipo_cambio"
                rules={[{ required: true, message: 'Indica el tipo de cambio' }]}
              >
                <InputNumber min={0} step={0.0001} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Monto"
                name="monto"
                rules={[
                  { required: true, message: 'Indica el monto del pago' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const saldoAnterior = Number(getFieldValue('saldo_anterior') || 0);
                      if (value == null) return Promise.resolve();
                      return Number(value) <= saldoAnterior
                        ? Promise.resolve()
                        : Promise.reject(new Error(`El monto no puede exceder el saldo disponible: ${saldoAnterior.toFixed(2)}`));
                    },
                  }),
                ]}
              >
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Title level={5} style={{ marginTop: 16 }}>Documento relacionado</Title>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Número de parcialidad"
                name="num_parcialidad"
                rules={[{ required: true, message: 'Indica la parcialidad' }]}
              >
                <InputNumber min={1} step={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Moneda del documento"
                name="moneda_dr"
                rules={[{ required: true, message: 'Selecciona la moneda del documento' }]}
              >
                <Select
                  options={[
                    { value: 'MXN', label: 'MXN' },
                    { value: 'USD', label: 'USD' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Tipo de cambio del documento (DR)"
                name="tipo_cambio_dr"
                dependencies={['moneda', 'moneda_dr']}
                rules={[
                  { required: true, message: 'Indica el tipo de cambio del documento' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const monedaP = getFieldValue('moneda');
                      const monedaDR = getFieldValue('moneda_dr');
                      if (!monedaP || !monedaDR) return Promise.resolve();
                      if (monedaP === monedaDR) {
                        return Number(value) === 1
                          ? Promise.resolve()
                          : Promise.reject(new Error('Cuando las monedas coinciden, el tipo de cambio DR debe ser 1.'));
                      }
                      return Number(value) > 0
                        ? Promise.resolve()
                        : Promise.reject(new Error('El tipo de cambio DR debe ser mayor a 0.'));
                    },
                  }),
                ]}
              >
                <InputNumber
                  min={0}
                  step={0.0001}
                  style={{ width: '100%' }}
                  disabled={drDisabled}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Saldo anterior"
                name="saldo_anterior"
                rules={[{ required: true, message: 'Indica el saldo anterior' }]}
              >
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} readOnly />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Importe pagado"
                name="importe_pagado"
                rules={[
                  { required: true, message: 'Indica el importe pagado' },
                  {
                    validator: (_, value) => {
                      const maxImporte = Number(form.getFieldValue('saldo_anterior') || 0);
                      if (value == null) return Promise.resolve();
                      return Number(value) <= maxImporte
                        ? Promise.resolve()
                        : Promise.reject(new Error(`No puedes exceder el faltante: ${maxImporte.toFixed(2)}`));
                    },
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                  max={Number(form.getFieldValue('saldo_anterior') || 0)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Saldo insoluto" name="saldo_insoluto">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} readOnly />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button onClick={() => setVisible(false)}>Cancelar</Button>
              <Button type="primary" loading={adding} onClick={handleSubmit}>
                Guardar pago
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AddPaymentModal;
